import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Navbar, Nav, NavItem, Glyphicon, Row, Col } from 'react-bootstrap';
import { browserHistory } from 'react-router';
import ReduxToastr, { toastr } from 'react-redux-toastr';
import { DragDropContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import _ from 'lodash';
import MainMenu from './MainMenu';
import LoadingProgressBar from './common/LoadingProgressBar';
import Button from './common/Button';
import FileDownload from './common/FileDownload';
import ServerError from './common/ServerError';
import ErrorBoundary from './common/ErrorBoundary';
import { userLogInLoadAction,
         userDismissFaxNotification,
         userFeedbackAction } from '../actions/userActions';

import { isEmptyObject, isEmptyString } from '../validators/formValidators';
import { caseSetServerError,
         caseEditIdAction, 
         casesFilterAction,
         casesRefreshNeededAction } from '../actions/caseActions';

import { clientsLoadAction, contactsLoadAction } from '../actions/clientActions';
import { userDownloadFile, userLogOutAction, serverLogOut } from '../actions/userActions';
import { clearClientToken, isImpersonating } from '../../client/token';
import { retrieveNotifications, isAdmin, filteredRoute } from '../helpers/utils';

import { isDemoAccount } from './../helpers/utils';
import AdminImpersonationBanner from './AdminImpersonationBanner';


class App extends PureComponent {
    constructor(props) {
        super(props);
        this.serverErrorClose = this.serverErrorClose.bind(this);
        this.notificationDismissed = this.notificationDismissed.bind(this);
        this.showNotifications = this.showNotifications.bind(this);
        this.logOut = this.logOut.bind(this);
        this.browseToCase = this.browseToCase.bind(this);
        this.signUpFromDemo = this.signUpFromDemo.bind(this);
        this.state = {
            loading: true,
            height: '',
            width: '',
            feedbackDialogOpen: false,
            helpDialogOpen: false,
            feedbackMessage: undefined,
            impersonating: false,
        };
    }

    // The theme is a per user setting; the last one is also kept in localStorage so the next page
    // load does not flash the other theme before the user record arrives.
    applyTheme() {
        if (!this.props.user) {
            return;
        }

        const theme = this.props.user['Theme'] === 'dark' ? 'dark' : 'light';

        document.documentElement.setAttribute('data-theme', theme);
        try {
            window.localStorage.setItem('ct-theme', theme);
        } catch (e) {}
    }

    componentDidUpdate() {
        this.applyTheme();
    }

    componentDidMount() {
        this.applyTheme();
        // Set client-only so the first client render matches the SSR markup.
        this.setState({ loading: false, impersonating: isImpersonating() });
        if (this.props.location === '/user/resetPassword') {
            return;
        }

        if (!this.props.user) {
            this.props.dispatch(userLogInLoadAction())
                .then((result) => {
                    if (isEmptyObject(result.data)) {
                        console.log('Logging out');
                        this.logOut();
                        return;
                    }

                    console.log(result);
                    retrieveNotifications(this.props.dispatch);
                })
                .catch((error) => {
                    console.error(new Error().stack, error);
                    this.props.dispatch(caseSetServerError(error));
                    this.logOut();
                });

        } else {
            retrieveNotifications(this.props.dispatch);
        }
    }

    componentWillReceiveProps(nextProps, nextState) {
        // A client was saved/imported/deleted: refresh the full list the contact pickers use.
        if (nextProps.user && nextProps.clientsRowsStale && !this.props.clientsRowsStale) {
            this.props.dispatch(clientsLoadAction());
        }

        if (!this.props.user && nextProps.user) {
            this.props.dispatch(clientsLoadAction());
            // The admin account has no cases of its own — its home is the
            // admin dashboard (unless it is impersonating someone).
            const location = nextProps.location || '';
            if (isAdmin(nextProps.user) && !isImpersonating() &&
                (location.startsWith('/case') || location.startsWith('/client'))) {
                browserHistory.push('/admin');
            }
        }
    }

    signUpFromDemo(evt) {
        if (evt) {
            evt.preventDefault();
        }

        // Leaving the demo for a real account: purge the sandbox server-side,
        // then hard-navigate to signup. A full page load (rather than an
        // in-app push + USER_LOG_OUT store reset) avoids the reset-mid-render
        // crash and guarantees we land on the real signup page.
        serverLogOut().finally(() => {
            clearClientToken();
            window.location.assign('/user/signup');
        });
    }

    browseToCase(caseId) {
        caseId = parseInt(caseId);
        this.props.dispatch(caseEditIdAction(caseId))
            .then((result) => {
                if (isEmptyObject(result.data)) {
                    this.props.dispatch(caseSetServerError('Given case doesn\'t seem to exist anymore.'));
                    this.notificationDismissed(caseId);
                    return;
                }

                this.notificationDismissed(caseId);
                let caseType = result.data && result.data.case && result.data.case.intake && result.data.case.intake['CaseType'];
                browserHistory.push(`/case/edit/intake/${caseId}`);
            })
            .catch((error) => {
                console.error(new Error().stack, error);
                this.props.dispatch(caseSetServerError(error));
            });
    }

    logOut(evt) {
        if (evt) {
            evt.preventDefault();
        }

        // Notify the server before dropping the token (purges temporary demo
        // accounts server-side; no-op for regular users).
        serverLogOut();

        clearClientToken();
        if (!this.props.location || !this.props.location.startsWith('/user/signup')) {
            browserHistory.push('/user/login');
        }

        this.props.dispatch(userLogOutAction());
    }

    notificationDismissed(caseId) {
        this.props.dispatch(userDismissFaxNotification(caseId))
            .catch((error) => {
                console.error(new Error().stack, error);
                sleep(process.env.CLIENT_POLLING_INTERVAL_MILISECONDS).then((result) => {
                    retrieveNotifications(this.props.dispatch);
                });
            });
    }

    showNotifications() {
        let failedFaxes = this.props.failedFaxes;
        if (!failedFaxes) {
            return;
        }

        let notificationsHash = {};
        let notifications = [];
        for (let i = 0; i < failedFaxes.length; i++) {
            if (notificationsHash[failedFaxes[i]['CaseId']]) {
                continue;
            }

            notificationsHash[failedFaxes[i]['CaseId']] = true;
            const toastrOptions = {
                timeOut: 0, // by setting to 0 it will prevent the auto close
                onHideComplete: () => this.notificationDismissed(failedFaxes[i]['CaseId']),
                showCloseButton: true, // true by default
                removeOnHover: false,
                component: (<Button bsStyle='link' onClick={() => this.browseToCase(failedFaxes[i]['CaseId'])} label="See more"/>)
            };

            toastr.error('Error', `Failed sending fax(es) for case id ${failedFaxes[i]['CaseId']}.`, toastrOptions)
        }
    }


    serverErrorClose() {
        this.props.dispatch(caseSetServerError(undefined));
    }


    render() {
        let title = '';
        if (this.props.user) {
            if (isAdmin(this.props.user)) {
                title = 'ADMIN';
                title = (<span style={{ color: 'red', fontWeight: 'bold' }}>{title}</span>);
            }
        }

        // The admin account gets a stripped-down menu: it only manages users
        // via the admin dashboard. (While impersonating, `user` is the
        // impersonated user, so the regular menu renders.)
        const adminSession = this.props.user && isAdmin(this.props.user);
        let menu = '';
        if (adminSession) {
            menu = (
                <React.Fragment>
                <Nav onSelect={route => browserHistory.push(route)}>
                    <NavItem data-hj-whitelist eventKey="/admin"><Glyphicon glyph="user" /> Users</NavItem>
                    <NavItem data-hj-whitelist eventKey="/admin/errors"><Glyphicon glyph="alert" /> Errors</NavItem>
                </Nav>
                <Nav pullRight>
                    <NavItem data-hj-whitelist onClick={() => browserHistory.push('/user/account')}><Glyphicon glyph="cog" /> Settings</NavItem>
                    <NavItem data-hj-whitelist onClick={this.logOut}><Glyphicon glyph="log-out" /> Log Out</NavItem>
                </Nav>
                </React.Fragment>
            );

        } else if (this.props.user) {
            menu = (<MainMenu {...this.props}/>);
        }

        let content;
        if (this.state.loading) {
            content = (
                <React.Fragment>
                <Row>
                    <Col xs={12}>
                        <LoadingProgressBar loading />
                    </Col>
                </Row>
                </React.Fragment>);

        } else {
            content = this.props.children;
        }

        this.showNotifications();

        // TODO(mobile): Notifications could easily occupy the whole screen.

        let downloadPath = this.props.downloadPath;
        let appBar = (
            <React.Fragment>
            {this.state.impersonating && (
                <AdminImpersonationBanner user={this.props.user} />
            )}
            <Navbar id="mainNavBar" staticTop collapseOnSelect>
                <Navbar.Header>
                    <Navbar.Brand style={{marginTop: '-15px'}}>
                        <Button bsStyle="link"
                                imgStyle={{ 
                                    margin: '0px',
                                    width: '112px',
                                }}
                                onClick={() => {
                                    if (adminSession) {
                                        browserHistory.push('/admin');
                                        return;
                                    }

                                    const route = filteredRoute('/case/all', {});
                                    this.props.dispatch(casesRefreshNeededAction(true));
                                    this.props.dispatch(casesFilterAction({}));
                                    browserHistory.push(route);
                                }}
                                img="/img/logo.png"
                                label={title}/>
                    </Navbar.Brand>
                    <Navbar.Toggle />
                </Navbar.Header>
                <Navbar.Collapse>
                    {menu}
                </Navbar.Collapse>
            </Navbar>
            {isDemoAccount(this.props.user) && (
                <div style={{
                    background: '#fff8e1',
                    borderBottom: '1px solid #f0e0a0',
                    color: '#6b5d1f',
                    textAlign: 'center',
                    padding: '6px 12px',
                    fontSize: '13px',
                }}>
                    You're exploring a <strong>live demo</strong> — emails, texts, faxes and uploads are simulated, nothing is really sent.
                    {' '}<a href="/user/signup" onClick={this.signUpFromDemo}>Get your own account</a>.
                </div>
            )}
            <ReduxToastr
                timeOut={0}
                newestOnTop
                preventDuplicates
                position="top-right"
                transitionIn="fadeIn"
                transitionOut="fadeOut"
                progressBar={false}
            />
            <ServerError error={this.props.serverError} style="popup" close={this.serverErrorClose} />
            <FileDownload downloadPath={downloadPath}
                          onDownloadCompleted={() => this.props.dispatch(userDownloadFile(undefined))} />
            </React.Fragment>
        );

        return (
            <ErrorBoundary>
            <div style={{overflowX: 'hidden', height: this.state.height, width: this.state.width}} ref='AppContainer'>
            {appBar}
            {content}
            </div>
            </ErrorBoundary>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    let location = '';
    let routingInfo = state.routing;
    if (routingInfo) {
        location = routingInfo.locationBeforeTransitions;
    }

    if (location) {
        location = location.pathname;
    }

    return {
        user: state.user.loggedIn,
        clientsRowsStale: state.clients.rowsStale,
        config: state.user.config,
        serverError: state.cases._serverError,
        failedFaxes: state.user.failedFaxes,
        downloadPath: state.user.downloadPath,
        location,
        routingInfo
    }
};

// Workaround for development to avoid:
// 'Error: Cannot have two HTML5 backends at the same time.'
// https://github.com/react-dnd/react-dnd/issues/894

let __DragDropContext = DragDropContext(HTML5Backend);
if (typeof window !== 'undefined') {
    window.__DragDropContext = window.__DragDropContext || DragDropContext(HTML5Backend);
    __DragDropContext = window.__DragDropContext;
}

export default _.flow(
  connect(mapStateToProps),
  __DragDropContext
)(App);